import { ArrowRightIcon, RotateCcwIcon, TriangleAlertIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { Link } from '@/components'
import { Button } from '@/components/ui'
import { useSupportTicket } from '@/modules/support'
import { getProjectPath } from '@/utils'
import { useStatements } from '../../hooks'
import { hasStatementFailed } from '../../status'
import { duplicatePageGroups } from '../duplicates/helpers'
import { ProcessButton } from '../ProcessButton'
import { cyclesNeedingReview } from '../reconciliation/helpers'

interface OverviewNoticesProps {
  slug: string
}

export function OverviewNotices({ slug }: OverviewNoticesProps) {
  const statements = useStatements()
  const { openTicket } = useSupportTicket()

  const failedStatements = statements.filter(hasStatementFailed)
  const repeatedPageCount = duplicatePageGroups(statements).length
  const reviewCycleCount = cyclesNeedingReview(statements)

  const noticeCount =
    (failedStatements.length > 0 ? 1 : 0) +
    (repeatedPageCount > 0 ? 1 : 0) +
    (reviewCycleCount > 0 ? 1 : 0)
  if (noticeCount === 0) return null

  return (
    <section className="overflow-hidden rounded-lg border border-red-200 bg-white">
      <header className="flex items-center gap-2 px-4 pt-3 pb-1">
        <TriangleAlertIcon className="size-4 text-red-600" />
        <h2 className="text-sm font-semibold text-red-900">Needs attention</h2>
      </header>
      <ul className="divide-y divide-neutral-100">
        {failedStatements.length > 0 && (
          <NoticeRow
            title={
              <>
                <span className="font-semibold text-red-600">
                  {failedStatements.length}
                </span>{' '}
                {failedStatements.length === 1 ? 'statement' : 'statements'}{' '}
                failed to process
              </>
            }
            subtitle="Reprocess to finish analyzing this project."
            footer={
              <span className="text-xs text-muted-foreground">
                Not working after a few retries?{' '}
                <button
                  type="button"
                  onClick={openTicket}
                  className="cursor-pointer font-medium text-red-600 underline-offset-2 hover:underline"
                >
                  Get help
                </button>
              </span>
            }
            action={
              <ProcessButton
                table={null}
                statementsToProcess={failedStatements}
                icon={RotateCcwIcon}
                theme="outline"
                size="sm"
                validationBehaviour="disable-only"
              >
                Retry all
              </ProcessButton>
            }
          />
        )}
        {repeatedPageCount > 0 && (
          <NoticeRow
            title={
              <>
                <span className="font-semibold text-amber-600">
                  {repeatedPageCount}
                </span>{' '}
                repeated page {repeatedPageCount === 1 ? 'range' : 'ranges'}{' '}
                found
              </>
            }
            subtitle="The same pages were uploaded more than once. Compare them and delete the extra copies."
            action={
              <Button asChild theme="outline" size="sm">
                <Link
                  to={`${getProjectPath({ slug })}/insights/duplicates`}
                  preload="intent"
                  className="no-underline hover:no-underline"
                >
                  Review
                  <ArrowRightIcon />
                </Link>
              </Button>
            }
          />
        )}
        {reviewCycleCount > 0 && (
          <NoticeRow
            title={
              <>
                <span className="font-semibold text-amber-600">
                  {reviewCycleCount}
                </span>{' '}
                statement {reviewCycleCount === 1 ? 'cycle' : 'cycles'} need
                {reviewCycleCount === 1 ? 's' : ''} reconciling
              </>
            }
            subtitle="The printed balances do not tie out to the transactions."
            action={
              <Button asChild theme="outline" size="sm">
                <Link
                  to={`${getProjectPath({ slug })}/insights/reconciliation`}
                  preload="intent"
                  className="no-underline hover:no-underline"
                >
                  Review & fix
                  <ArrowRightIcon />
                </Link>
              </Button>
            }
          />
        )}
      </ul>
    </section>
  )
}

interface NoticeRowProps {
  title: ReactNode
  subtitle: string
  action: ReactNode
  footer?: ReactNode
}

function NoticeRow({ title, subtitle, action, footer }: NoticeRowProps) {
  return (
    <li className="flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-neutral-50/60">
      <div className="leading-tight">
        <p className="text-sm text-foreground">{title}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <span>{subtitle}</span>
          {footer}
        </div>
      </div>
      {action}
    </li>
  )
}
